
import React, { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Upload } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Progress } from "@/components/ui/progress";

interface FileUploadSectionProps {
  preview: string | null;
  fileName: string | null;
  uploading?: boolean;
  uploadProgress?: number;
  onFileChange: (file: File | null) => void;
  onRemove: () => void;
  disabled?: boolean;
}

export const FileUploadSection: React.FC<FileUploadSectionProps> = ({
  preview,
  fileName,
  uploading = false,
  uploadProgress = 0,
  onFileChange,
  onRemove,
  disabled = false,
}) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleFileChange = (file: File | null) => {
    if (file) {
      if (!file.type.startsWith("image/")) {
        return;
      }
      onFileChange(file);
    }
  };

  const onDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (disabled || uploading) return;
    
    const file = e.dataTransfer.files[0];
    handleFileChange(file);
  }, [disabled, uploading, onFileChange]);

  const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  return (
    <div className="space-y-2">
      <div className="font-medium text-sm">Photo</div>
      <div 
        className={`border-2 border-dashed rounded-md p-4 text-center space-y-4 ${!disabled && !uploading ? 'cursor-pointer hover:border-primary hover:bg-accent/50' : 'bg-[#f0f0f0] cursor-not-allowed border-gray-300'}`}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onClick={() => !disabled && !uploading && fileInputRef.current?.click()}
      >
        {preview ? (
          <div className="space-y-4">
            {/* Reduced preview size by 25% */}
            <div className="max-w-[56%] mx-auto">
              <AspectRatio ratio={16/9} className="bg-gray-100 overflow-hidden rounded-md">
                <img 
                  src={preview} 
                  alt="Preview" 
                  className="w-full h-full object-contain"
                />
              </AspectRatio>
            </div>
            <div className="flex items-center justify-center gap-2">
              <p className="text-sm text-gray-500 truncate max-w-[200px]">
                {fileName}
              </p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
                disabled={disabled || uploading}
              >
                Remove
              </Button>
            </div>
            {uploading && (
              <div className="space-y-2">
                <Progress value={uploadProgress} className="h-2" />
                <div className="flex items-center justify-center text-sm">
                  <span className="text-gray-500">
                    {uploadProgress < 100 ? "Uploading..." : "Processing..."}
                  </span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className="flex flex-col items-center space-y-2">
              <Upload className={`h-8 w-8 ${disabled ? 'text-[#888888]' : 'text-gray-400'}`} />
              <span className={`font-medium ${disabled ? 'text-[#888888]' : ''}`}>Drag and drop or click to upload</span>
              <span className={`text-xs ${disabled ? 'text-[#888888]' : 'text-gray-500'}`}>SVG, PNG, JPG or GIF (max. 8MB)</span>
            </div>
            <input
              type="file"
              accept="image/*"
              className="sr-only"
              onChange={(e) => e.target.files && handleFileChange(e.target.files[0])}
              ref={fileInputRef}
              disabled={disabled}
            />
          </div>
        )}
      </div>
    </div>
  );
};
